import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DateRange, Preset } from '../types';
import { startOfMonth, endOfMonth } from '../constants';
import { normalize } from '../utils';

interface UseDurationSelectorProps {
  initialValue: DateRange;
  initialMonth?: Dayjs;
  onChange?: (range: DateRange) => void;
}

export function useDurationSelector({
  initialValue,
  initialMonth,
  onChange,
}: UseDurationSelectorProps) {
  const [range, setRange] = React.useState<DateRange>(initialValue);
  const [selectedPreset, setSelectedPreset] = React.useState<string | null>(
    null,
  );
  const [leftMonth, setLeftMonth] = React.useState<Dayjs>(
    initialMonth || startOfMonth(dayjs()),
  );

  React.useEffect(() => {
    setRange(initialValue);
  }, [initialValue[0]?.valueOf(), initialValue[1]?.valueOf()]);

  const updateRange = React.useCallback(
    (newRange: DateRange) => {
      setRange(newRange);
      onChange?.(newRange);
    },
    [onChange],
  );

  const handleSelectDay = React.useCallback(
    (day: Dayjs) => {
      const [start, end] = range;
      setSelectedPreset(null);

      if (!start || (start && end)) {
        updateRange([day.startOf('day'), null]);
      } else {
        updateRange(normalize(start, day));
      }
    },
    [range, updateRange],
  );

  const handlePresetClick = React.useCallback(
    (preset: Preset) => {
      const newRange = preset.getRange();
      setRange(newRange);
      setSelectedPreset(preset.id);

      if (newRange[0]) {
        setLeftMonth(newRange[0].startOf('month'));
      }

      onChange?.(newRange);
    },
    [onChange],
  );

  const handleReset = React.useCallback(() => {
    const emptyRange: DateRange = [null, null];
    setRange(emptyRange);
    setSelectedPreset(null);
    setLeftMonth(startOfMonth());
    onChange?.(emptyRange);
  }, [onChange]);

  return {
    range,
    selectedPreset,
    leftMonth,
    setRange,
    setLeftMonth,
    handleSelectDay,
    handlePresetClick,
    handleReset,
    updateRange,
  };
}
